// src/pages/parts/orderPickup/components/DeliveryNoteTable.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { BlockUI } from 'primereact/blockui';
import { ProgressSpinner } from 'primereact/progressspinner';
import DeliveryNoteDialog from './DeliveryNoteDialog';
import GenericFileExportButton from '../../../../components/common/GenericFileExportButton';
import { useGetDeliveryNotePickupListQuery, useLazyExportDeliveryNotePickupListToExcelQuery } from '../../../../api/endpoints/parts';
import { useLanguage } from '../../../../contexts/LanguageContext';
import GenericTableFilter from '../../../../components/common/GenericTableFilter';

const DeliveryNoteTable = () => {
  const { th } = useLanguage();
  const [dialogVisible, setDialogVisible] = useState(false);
  const [serialNumber, setSerialNumber] = useState('');
  const [receivedTabIndex, setReceivedTabIndex] = useState(0);
  const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1, sortField: null, sortOrder: null });
  const [activeFilters, setActiveFilters] = useState({ invoiceNo: '' });
  const [isExporting, setIsExporting] = useState(false);

  const isReceived = receivedTabIndex === 1;
  const excelParams = {
    Received: isReceived,
    ...(activeFilters.invoiceNo ? { Dln: activeFilters.invoiceNo } : {}),
  };

  useEffect(() => {
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = isExporting ? 'wait' : '';
    return () => {
      document.body.style.cursor = previousCursor;
    };
  }, [isExporting]);

  const filterFields = useMemo(
    () => [
      { field: 'invoiceNo', headerName: th('table.deliveryNoteNo'), type: 'text' },
    ],
    [th]
  );

  const commonArgs = {
    PageNumber: lazyParams.page,
    PageSize: lazyParams.rows,
    Dln: activeFilters.invoiceNo || '',
    Received: isReceived,
  };
  const { data: deliveryNoteData, isFetching } = useGetDeliveryNotePickupListQuery(commonArgs, {
    refetchOnMountOrArgChange: true,
  });

  const rawItems = deliveryNoteData?.data?.items ?? [];
  const totalRecords = deliveryNoteData?.data?.totalCount ?? 0;

  const sortedData = useMemo(() => {
    if (!lazyParams.sortField) return rawItems;
    const order = lazyParams.sortOrder === 1 ? 1 : -1;
    return [...rawItems].sort((a, b) => {
      const aVal = a[lazyParams.sortField] ?? '';
      const bVal = b[lazyParams.sortField] ?? '';
      return order * aVal.toString().localeCompare(bVal.toString(), 'tr', { numeric: true });
    });
  }, [rawItems, lazyParams.sortField, lazyParams.sortOrder]);

  const onPage = (event) => {
    const calculatedPage = Math.floor(event.first / event.rows) + 1;
    setLazyParams((prev) => ({ ...prev, first: event.first, rows: event.rows, page: calculatedPage }));
  };

  const onSort = (event) => {
    setLazyParams((prev) => ({ ...prev, sortField: event.sortField, sortOrder: event.sortOrder }));
  };

  const handleEdit = (row) => {
    setSerialNumber(row.invoiceNo || '');
    setDialogVisible(true);
  };

  useEffect(() => {
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
  }, [activeFilters, receivedTabIndex]);

  const actionBodyTemplate = (row) => (
    <Button icon="pi pi-pencil" rounded text aria-label={th('parts.edit')} onClick={() => handleEdit(row)} />
  );

  const handleAdvancedFilterChange = (updated) => {
    setActiveFilters({ invoiceNo: updated.invoiceNo || '' });
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
  };

  const initFilters = () => {
    setActiveFilters({ invoiceNo: '' });
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1, sortField: null, sortOrder: null }));
  };

  const handleReceivedTabChange = (e) => {
    setReceivedTabIndex(e.index);
    setLazyParams((prev) => ({ ...prev, first: 0, page: 1, sortField: null, sortOrder: null }));
  };

  const tableContent = (
    <>
      <div className="mb-2">
        <GenericTableFilter
          filterFields={filterFields}
          initialFilters={{ invoiceNo: '' }}
          onApplyFilters={handleAdvancedFilterChange}
          onResetFilters={initFilters}
        />
      </div>

      <DataTable
        value={sortedData}
        lazy
        paginator
        first={lazyParams.first}
        rows={lazyParams.rows}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        className="p-datatable-gridlines"
        showGridlines
        dataKey="invoiceNo"
        loading={isFetching}
        emptyMessage={th('parts.invoiceNotFound')}
        rowsPerPageOptions={[5, 10, 25, 50]}
        tableStyle={{ minWidth: '50rem' }}
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        currentPageReportTemplate={th('table.paginatorTemplate')}
      >
        <Column body={actionBodyTemplate} style={{ width: '5%' }} align="center" />
        <Column field="invoiceNo" header={th('table.deliveryNoteNo')} sortable style={{ width: '30%' }} />
        <Column
          field="invoiceDate"
          header={th('table.deliveryNoteDate')}
          sortable
          body={(row) => (row.invoiceDate ? new Date(row.invoiceDate).toLocaleDateString('tr-TR') : '')}
          style={{ width: '20%' }}
        />
        <Column field="partCount" header={th('parts.quantity')} sortable style={{ width: '20%' }} />
        <Column field="remainingPart" header={th('order.remainingQty')} sortable style={{ width: '20%' }} />
      </DataTable>
    </>
  );

  const excelContentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

  return (
    <div style={isExporting ? { cursor: 'wait' } : undefined}>
      <BlockUI
        blocked={isExporting}
        template={
          <div className="flex flex-column align-items-center gap-2">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
            <span>{th('common.downloading')}</span>
          </div>
        }
      >
      <div className="flex justify-content-end mb-3">
        <GenericFileExportButton
          useExportQuery={useLazyExportDeliveryNotePickupListToExcelQuery}
          exportParams={excelParams}
          buttonLabel={th('common.export')}
          buttonClassName="p-button-success"
          icon="pi pi-file-excel"
          disabled={isExporting}
          defaultFileName="IrsaliyeTeslimAlma"
          defaultContentType={excelContentType}
          dataExtractor={(result) => result?.fileBase64}
          fileNameExtractor={(result) => result?.fileName}
          contentTypeExtractor={(result) => result?.contentType || excelContentType}
          onExportStart={() => setIsExporting(true)}
          onExportSuccess={() => setIsExporting(false)}
          onExportError={() => setIsExporting(false)}
        />
      </div>
      <TabView activeIndex={receivedTabIndex} onTabChange={handleReceivedTabChange}>
        <TabPanel header={th('parts.notReceivedTab')}>
          {tableContent}
        </TabPanel>
        <TabPanel header={th('parts.receivedTab')}>
          {tableContent}
        </TabPanel>
      </TabView>

      <DeliveryNoteDialog
        key={dialogVisible ? (serialNumber || 'new-delivery') : 'hidden'}
        visible={dialogVisible}
        onHide={() => setDialogVisible(false)}
        deliveryNoteNo={serialNumber}
      />
      </BlockUI>
    </div>
  );
};

export default DeliveryNoteTable;
